import * as Sentry from '@sentry/nextjs';
import axios from 'axios';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AlertTypes, openAlert } from '../store/alert';
import { setAuthUser, setToken } from '../store/auth';
import { Cookies, deleteCookie, getCookie, setCookie } from './cookie';
import { isSessionlessRoute } from './sessionlessRoutes';

interface useAuthPayload {
  isAuthFetching: boolean;
  authError: any;
}

const capitalizeFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const useAuth = (): useAuthPayload => {
  const dispatch = useDispatch();
  const [isAuthFetching, setIsAuthFetching] = useState(true);
  const [authError, setAuthError] = useState(false);
  const router = useRouter();
  const isSessionless = isSessionlessRoute(router.pathname);

  const dispatchAuthUser = useCallback(
    (data: any) => {
      const {
        _id,
        role,
        fullName,
        username,
        image,
        imagePublicId,
        dsrbThumbnailPublicId,
        thumbnailLargePublicId,
        coverImage,
        coverImagePublicId,
        facebookId,
        googleId,
        githubId,
        about,
        website,
        email,
        notifications,
        followers,
        following,
        isActive,
        banned,
        forumPostingRestricted,
        forumGuidelinesAcceptedAt,
        forumBusinessName,
        forumLogo,
        forumLogoPublicId,
        currentMembership,
        sstRole,
      } = data;

      // Set Sentry user context for error tracking
      Sentry.setUser({
        id: _id,
        email: email,
        username: username || fullName,
      });

      dispatch(
        setAuthUser({
          _id,
          role,
          image,
          imagePublicId,
          dsrbThumbnailPublicId,
          thumbnailLargePublicId,
          coverImage,
          coverImagePublicId,
          fullName,
          username,
          facebookId,
          googleId,
          githubId,
          about,
          website,
          email,
          notifications,
          followers,
          following,
          isOnline: true,
          isActive,
          banned,
          forumPostingRestricted: Boolean(forumPostingRestricted),
          forumGuidelinesAcceptedAt: forumGuidelinesAcceptedAt
            ? new Date(forumGuidelinesAcceptedAt).toISOString()
            : undefined,
          forumBusinessName,
          forumLogo,
          forumLogoPublicId,
          currentMembership,
          sstRole,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (!router.query?.auth || router.query?.auth !== 'social') {
      return;
    }

    const responseType = router.query?.responseType;
    const provider = router.query?.provider;

    if (responseType !== 'success' && responseType !== 'error') {
      return;
    }

    if (!provider) {
      return;
    }

    if (responseType === 'error') {
      dispatch(
        openAlert({
          type: AlertTypes.Error,
          message: `An error occurred while signing in with ${capitalizeFirstLetter(provider as string)}.`,
        })
      );
      return;
    }

    const token = router.query?.token as string;
    if (!token) {
      return;
    }

    const fetch = async () => {
      axios.defaults.headers.common = { Authorization: `bearer ${token}` };
      const { data } = await axios.put('/auth-user');
      if (!data) {
        return;
      }

      setCookie(Cookies.Token, token);
      dispatch(setToken(token));
      dispatchAuthUser(data);
      router.replace('/', undefined, { shallow: true });
    };

    fetch();
  }, [router, dispatch, dispatchAuthUser]);

  useEffect(() => {
    if (isSessionless) {
      setIsAuthFetching(false);
      return;
    }

    const fetch = async () => {
      setIsAuthFetching(true);
      try {
        const token = getCookie(Cookies.Token);
        if (!token) {
          // Clear Sentry user context when not authenticated
          Sentry.setUser(null);
          setIsAuthFetching(false);
          return;
        }

        axios.defaults.headers.common = { Authorization: `bearer ${token}` };
        const { data } = await axios.put('/auth-user');
        if (!data) {
          // Clear Sentry user context if auth fails
          Sentry.setUser(null);
          setIsAuthFetching(false);
          return;
        }
        dispatchAuthUser(data);
        setAuthError(null);
      } catch (error) {
        // Clear Sentry user context on error/logout
        Sentry.setUser(null);
        // I believe there's an issue with the timeout of the cookie and it's generating this issue.
        deleteCookie(Cookies.Token);
        await axios.post('/logout');
        window.location.href = '/login';
        // setAuthError(error);
      } finally {
        setIsAuthFetching(false);
      }
    };

    fetch();
  }, [dispatch, dispatchAuthUser, isSessionless]);

  return { isAuthFetching, authError };
};

export default useAuth;
